/**
 * BookingPage
 *
 * Public (no auth) page for booking a meeting via a scheduling link.
 * Shows a month calendar view with available days highlighted.
 * Steps: browse calendar + pick slot -> fill in details -> confirmation.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import type { PublicSchedulingInfo, AvailableSlotDetail } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Clock, MapPin, Loader2, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';

type Step = 'loading' | 'select' | 'form' | 'confirming' | 'confirmed' | 'error';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

function toDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getMonthGrid(year: number, month: number): (Date | null)[][] {
  const firstDay = new Date(year, month, 1);
  const startWeekday = firstDay.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const weeks: (Date | null)[][] = [];
  let week: (Date | null)[] = new Array(startWeekday).fill(null);

  for (let day = 1; day <= daysInMonth; day++) {
    week.push(new Date(year, month, day));
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  return weeks;
}

export function BookingPage() {
  const { slug } = useParams<{ slug: string }>();
  const [step, setStep] = useState<Step>('loading');
  const [info, setInfo] = useState<PublicSchedulingInfo | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [slots, setSlots] = useState<AvailableSlotDetail[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlotDetail | null>(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Calendar month navigation
  const today = useMemo(() => new Date(), []);
  const todayKey = useMemo(() => toDateKey(today), [today]);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  // Form
  const [bookerName, setBookerName] = useState('');
  const [bookerEmail, setBookerEmail] = useState('');
  const [notes, setNotes] = useState('');

  // Confirmation
  const [confirmationTime, setConfirmationTime] = useState({ start: '', end: '' });

  // Available days of the week (from scheduling link config)
  const availableDaysOfWeek = useMemo(() => {
    if (!info) return new Set<number>();
    return new Set(info.schedulingLink.availabilityWindows.map((w) => w.dayOfWeek));
  }, [info]);

  // Load initial scheduling info (no date needed - just to get link metadata)
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await api.getPublicSchedulingInfo(
          slug,
          toDateKey(today),
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        );
        if (!cancelled) {
          setInfo(result);
          setStep('select');
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMessage(err instanceof Error ? err.message : 'Scheduling link not found');
          setStep('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, today]);

  const fetchSlots = useCallback(
    async (date: string) => {
      if (!slug) return;
      setSlotsLoading(true);
      setSlots([]);
      try {
        const result = await api.getPublicSchedulingInfo(
          slug,
          date,
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        );
        setInfo(result);
        setSlots(result.availableSlots);
      } catch {
        setSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    },
    [slug],
  );

  const handleSelectDate = (date: Date) => {
    const key = toDateKey(date);
    setSelectedDate(key);
    setSelectedSlot(null);
    fetchSlots(key);
  };

  const handleSelectSlot = (slot: AvailableSlotDetail) => {
    setSelectedSlot(slot);
    setStep('form');
  };

  const handleBook = async () => {
    if (!slug || !selectedSlot || !bookerName.trim() || !bookerEmail.trim()) return;

    setStep('confirming');
    try {
      const result = await api.createPublicBooking(slug, {
        bookerName: bookerName.trim(),
        bookerEmail: bookerEmail.trim(),
        startAt: selectedSlot.startAt,
        notes: notes.trim() || undefined,
      });
      setConfirmationTime({ start: result.booking.startAt, end: result.booking.endAt });
      setStep('confirmed');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to book');
      setStep('error');
    }
  };

  const navigateMonth = (direction: number) => {
    let newMonth = viewMonth + direction;
    let newYear = viewYear;
    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }
    setViewYear(newYear);
    setViewMonth(newMonth);
  };

  const canGoBack =
    viewYear > today.getFullYear() ||
    (viewYear === today.getFullYear() && viewMonth > today.getMonth());

  const monthGrid = useMemo(() => getMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const monthLabel = new Date(viewYear, viewMonth).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  const isDayAvailable = (date: Date): boolean => {
    const key = toDateKey(date);
    if (key < todayKey) return false;
    return availableDaysOfWeek.has(date.getDay());
  };

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

  const formatDate = (dateStr: string) =>
    new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });

  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-2">
          <p className="text-lg font-medium text-destructive">{errorMessage}</p>
          <Button
            variant="outline"
            onClick={() => {
              setStep('select');
              setErrorMessage('');
            }}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (step === 'confirmed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md w-full mx-4 text-center space-y-4">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
          <h1 className="text-2xl font-bold">Booking Confirmed!</h1>
          <div className="space-y-1 text-muted-foreground">
            <p>{info?.schedulingLink.title}</p>
            <p className="font-medium text-foreground">
              {formatDate(confirmationTime.start.split('T')[0])}
            </p>
            <p>
              {formatTime(confirmationTime.start)} - {formatTime(confirmationTime.end)}
            </p>
            {info?.schedulingLink.location && (
              <p className="flex items-center justify-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {info.schedulingLink.location}
              </p>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            A confirmation has been sent. Save your cancellation link if you need to reschedule.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-2xl w-full space-y-6">
        {/* Header */}
        <div className="text-center space-y-1">
          {info?.schedulingLink.color && (
            <div
              className="w-10 h-1 rounded mx-auto mb-3"
              style={{ backgroundColor: info.schedulingLink.color }}
            />
          )}
          <h1 className="text-2xl font-bold">{info?.schedulingLink.title}</h1>
          {info?.schedulingLink.description && (
            <p className="text-sm text-muted-foreground">{info.schedulingLink.description}</p>
          )}
          <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {info?.schedulingLink.durationMinutes} min
            </span>
            {info?.schedulingLink.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {info.schedulingLink.location}
              </span>
            )}
          </div>
        </div>

        {step === 'select' && (
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Month calendar */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => navigateMonth(-1)}
                  disabled={!canGoBack}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium">{monthLabel}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => navigateMonth(1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Weekday headers */}
              <div className="grid grid-cols-7 gap-0.5 mb-1">
                {WEEKDAY_LABELS.map((label) => (
                  <div
                    key={label}
                    className="text-center text-xs font-medium text-muted-foreground py-1"
                  >
                    {label}
                  </div>
                ))}
              </div>

              {/* Day cells */}
              {monthGrid.map((week, wi) => (
                <div key={wi} className="grid grid-cols-7 gap-0.5">
                  {week.map((date, di) => {
                    if (!date) {
                      return <div key={di} className="aspect-square" />;
                    }

                    const key = toDateKey(date);
                    const isToday = key === todayKey;
                    const available = isDayAvailable(date);
                    const isSelected = key === selectedDate;

                    return (
                      <button
                        key={di}
                        type="button"
                        disabled={!available}
                        onClick={() => handleSelectDate(date)}
                        className={`
                          aspect-square flex items-center justify-center rounded-md text-sm transition-colors
                          ${isSelected ? 'bg-primary text-primary-foreground font-semibold' : ''}
                          ${!isSelected && available ? 'hover:bg-accent cursor-pointer font-medium' : ''}
                          ${!available ? 'text-muted-foreground/40 cursor-default' : ''}
                          ${isToday && !isSelected ? 'ring-1 ring-primary' : ''}
                        `}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Time slots panel */}
            <div className="sm:w-52 shrink-0">
              {!selectedDate ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Select a date to see available times.
                </p>
              ) : slotsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <p className="text-sm font-medium mb-3">{formatDate(selectedDate)}</p>
                  {slots.length > 0 ? (
                    <div className="space-y-1.5 max-h-72 overflow-y-auto">
                      {slots.map((slot) => (
                        <Button
                          key={slot.startAt}
                          variant="outline"
                          size="sm"
                          className="w-full text-sm justify-center"
                          onClick={() => handleSelectSlot(slot)}
                        >
                          {formatTime(slot.startAt)}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No available times on this date.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {(step === 'form' || step === 'confirming') && selectedSlot && selectedDate && (
          <div className="max-w-md mx-auto space-y-4">
            <div className="bg-muted/50 rounded-md p-3 text-sm">
              <p className="font-medium">{formatDate(selectedDate)}</p>
              <p className="text-muted-foreground">
                {formatTime(selectedSlot.startAt)} - {formatTime(selectedSlot.endAt)}
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-sm">Name</Label>
                <Input
                  value={bookerName}
                  onChange={(e) => setBookerName(e.target.value)}
                  placeholder="Your name"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-sm">Email</Label>
                <Input
                  type="email"
                  value={bookerEmail}
                  onChange={(e) => setBookerEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <Label className="text-sm">Notes (optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Anything you'd like to share in advance"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedSlot(null);
                  setStep('select');
                }}
                disabled={step === 'confirming'}
              >
                Back
              </Button>
              <Button
                className="flex-1"
                onClick={handleBook}
                disabled={step === 'confirming' || !bookerName.trim() || !bookerEmail.trim()}
              >
                {step === 'confirming' && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Confirm Booking
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
